import React from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import CountUp from 'react-countup';
import { formatCurrency } from './useDistribuidorCalculos';

/**
 * Componente que renderiza el panel de métricas e indicadores financieros (columna izquierda):
 * - Número principal con CountUp para el Promedio Trimestral y badge de variación QoQ
 * - Bloque interactivo para Total Pagado del Mes con badge de variación MoM
 * - Bloque interactivo para Promedio de 3 Meses Anteriores con ratio de meses activos
 */
const DistribuidorGraficaMetricas = ({
    cargando = false,
    promedioUltimos3 = 0,
    promedioAnteriores3 = 0,
    totalMesActual = 0,
    totalMesAnterior = 0,
    etiquetaMesActual = 'Mes Actual',
    etiquetaMesAnterior = 'mes anterior',
    variacionMoM = 0,
    esMoMPositivo = true,
    variacionQoQ = 0,
    esQoQPositivo = true,
    mesesConCompra = 0,
    visibleSeries = { mesActual: true, promedioUltimos3: true, promedioAnteriores3: true },
    toggleSeries = () => {},
    lineColor = '#6366f1'
}) => {
    return (
        <div className="d-flex flex-column justify-content-center pe-md-2">
            {/* 1. Número grande: Promedio 3 Meses (Línea Morada) */}
            <div
                role="button"
                onClick={() => toggleSeries('mesActual')}
                title="Clic para mostrar / ocultar en la gráfica"
                style={{
                    cursor: 'pointer',
                    opacity: visibleSeries.mesActual ? 1 : 0.45,
                    transition: 'opacity 0.2s'
                }}
            >
                <div className="d-flex align-items-baseline gap-2 mb-1 flex-wrap">
                    <div
                        className="fw-extrabold text-1000"
                        style={{
                            fontSize: '2.15rem',
                            lineHeight: '1.1',
                            fontWeight: 800
                        }}
                    >
                        {cargando ? (
                            <Spinner animation="border" size="sm" variant="secondary" />
                        ) : (
                            <CountUp
                                start={0}
                                end={promedioUltimos3}
                                duration={1.2}
                                separator=","
                                decimals={2}
                                prefix="$"
                            />
                        )}
                        <span className="text-600" style={{ fontSize: '0.9rem', fontWeight: 600, marginLeft: '6px' }}>
                            MXN
                        </span>
                    </div>

                    {/* Badge de Variación Trimestral QoQ */}
                    {!cargando && promedioAnteriores3 > 0 && (
                        <span
                            className={`badge rounded-pill fw-bold ${
                                esQoQPositivo
                                    ? 'badge-subtle-success text-success'
                                    : 'badge-subtle-danger text-danger'
                            }`}
                            style={{
                                fontSize: '0.76rem',
                                padding: '4px 8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                            title={`Comparado contra el promedio del trimestre anterior (${formatCurrency(promedioAnteriores3)})`}
                        >
                            <FontAwesomeIcon
                                icon={esQoQPositivo ? faArrowUp : faArrowDown}
                                style={{ fontSize: '0.65rem' }}
                            />
                            {esQoQPositivo ? `+${variacionQoQ.toFixed(1)}%` : `${variacionQoQ.toFixed(1)}%`} trim.
                        </span>
                    )}
                </div>

                {/* Subtítulo indicando que el número grande es el promedio */}
                <div
                    className="d-flex align-items-center gap-1 mb-2 fw-semibold text-600"
                    style={{ fontSize: '0.75rem' }}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: lineColor,
                            display: 'inline-block'
                        }}
                    />
                    <span>Promedio 3 Meses ({etiquetaMesActual})</span>
                    <span style={{ fontSize: '0.68rem', opacity: 0.75 }}>
                        {visibleSeries.mesActual ? '(visible)' : '(oculto)'}
                    </span>
                </div>
            </div>

            {/* 2. Bloques interactivos: Total Pagado del Mes (Verde) y Promedio 3 Meses Anteriores (Ámbar) */}
            <div className="d-flex flex-column gap-1 pt-2 border-top border-200">
                {/* Total Pagado del Mes */}
                <div
                    role="button"
                    onClick={() => toggleSeries('promedioUltimos3')}
                    title="Clic para mostrar / ocultar en la gráfica"
                    className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2"
                    style={{
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        opacity: visibleSeries.promedioUltimos3 ? 1 : 0.45,
                        backgroundColor: visibleSeries.promedioUltimos3
                            ? 'rgba(16, 185, 129, 0.12)'
                            : 'transparent',
                        border: visibleSeries.promedioUltimos3
                            ? '1px solid rgba(16, 185, 129, 0.35)'
                            : '1px solid transparent',
                        transition: 'all 0.2s'
                    }}
                >
                    <div className="d-flex align-items-center gap-1 text-800">
                        <span
                            style={{
                                width: 12,
                                height: 2,
                                borderTop: '2px dashed #10b981',
                                display: 'inline-block'
                            }}
                        />
                        <span>-- Total Pagado en {etiquetaMesActual}</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span
                            className="fw-bold"
                            style={{
                                color: visibleSeries.promedioUltimos3 ? '#10b981' : undefined
                            }}
                        >
                            {formatCurrency(totalMesActual)}
                        </span>
                        {totalMesAnterior > 0 && (
                            <span
                                className={`badge rounded-pill fw-bold ${
                                    esMoMPositivo
                                        ? 'badge-subtle-success text-success'
                                        : 'badge-subtle-danger text-danger'
                                }`}
                                style={{ fontSize: '0.68rem', padding: '2px 5px' }}
                                title={`Variación mensual vs ${etiquetaMesAnterior} (${formatCurrency(totalMesAnterior)})`}
                            >
                                <FontAwesomeIcon
                                    icon={esMoMPositivo ? faArrowUp : faArrowDown}
                                    style={{ fontSize: '0.58rem', marginRight: '2px' }}
                                />
                                {esMoMPositivo ? `+${variacionMoM.toFixed(1)}%` : `${variacionMoM.toFixed(1)}%`}
                            </span>
                        )}
                    </div>
                </div>

                {/* Promedio 3 Meses Anteriores */}
                <div
                    role="button"
                    onClick={() => toggleSeries('promedioAnteriores3')}
                    title="Clic para mostrar / ocultar en la gráfica"
                    className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2"
                    style={{
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        opacity: visibleSeries.promedioAnteriores3 ? 1 : 0.45,
                        backgroundColor: visibleSeries.promedioAnteriores3
                            ? 'rgba(245, 158, 11, 0.12)'
                            : 'transparent',
                        border: visibleSeries.promedioAnteriores3
                            ? '1px solid rgba(245, 158, 11, 0.35)'
                            : '1px solid transparent',
                        transition: 'all 0.2s'
                    }}
                >
                    <div className="d-flex align-items-center gap-1 text-800">
                        <span
                            style={{
                                width: 12,
                                height: 2,
                                borderTop: '2px dashed #f59e0b',
                                display: 'inline-block'
                            }}
                        />
                        <span>-- Prom. 3 Meses Anteriores</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <span
                            className="fw-bold"
                            style={{
                                color: visibleSeries.promedioAnteriores3 ? '#f59e0b' : undefined
                            }}
                        >
                            {formatCurrency(promedioAnteriores3)}
                        </span>
                        <span
                            className="badge rounded-pill bg-200 text-700 fw-semibold"
                            style={{ fontSize: '0.68rem', padding: '2px 5px' }}
                            title="Meses con compras en el trimestre actual"
                        >
                            {mesesConCompra}/3 activos
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

DistribuidorGraficaMetricas.propTypes = {
    cargando: PropTypes.bool,
    promedioUltimos3: PropTypes.number,
    promedioAnteriores3: PropTypes.number,
    totalMesActual: PropTypes.number,
    totalMesAnterior: PropTypes.number,
    etiquetaMesActual: PropTypes.string,
    etiquetaMesAnterior: PropTypes.string,
    variacionMoM: PropTypes.number,
    esMoMPositivo: PropTypes.bool,
    variacionQoQ: PropTypes.number,
    esQoQPositivo: PropTypes.bool,
    mesesConCompra: PropTypes.number,
    visibleSeries: PropTypes.object,
    toggleSeries: PropTypes.func,
    lineColor: PropTypes.string
};

export default DistribuidorGraficaMetricas;
